<script setup lang="ts">
import EmailVerification from "@/components/EmailVerification.vue";
import EmailVerificationSuccess from "@/components/EmailVerificationSuccess.vue";
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import { applyActionCode, checkActionCode } from "firebase/auth";
import { auth } from "@/firebase";

const router = useRouter();
const route = useRoute();

onMounted(async () => {
  document.title = "ElevatED – Verify Email";

  // Reset verification state on mount to ensure fresh verification
  emailVerificationData.value.verificationCode = "";

  // Update email from route query if available
  if (route.query.email) {
    emailVerificationData.value.email = route.query.email as string;
  }

  // Check if there's an oobCode from Firebase email verification link
  const oobCode = route.query.oobCode as string;
  if (oobCode) {
    isProcessingLink.value = true;
    await handleEmailVerificationLink(oobCode);
  }
});

// Data for the EmailVerification component
const emailVerificationData = ref({
  verificationCode: "",
  email: (route.query.email as string) || "user@example.com",
});

// State to track verification status
const isEmailVerified = ref(false);
const verificationError = ref("");
const isProcessingLink = ref(false);

// Handle email verification link from Firebase
const handleEmailVerificationLink = async (oobCode: string) => {
  try {
    console.log("Verifying email with oobCode:", oobCode);
    
    // First check if the action code is valid
    const actionCodeInfo = await checkActionCode(auth, oobCode);
    console.log("Action code info:", actionCodeInfo);
    
    // Apply the action code to verify the email
    await applyActionCode(auth, oobCode);
    console.log("Email verification successful!");
    
    // Update the verification status in Firestore
    if (actionCodeInfo.data?.email) {
      await updateUserVerificationStatus(actionCodeInfo.data.email);
    }
    
    // Set verification success state
    isEmailVerified.value = true;
    verificationError.value = "";
    
    // Determine redirect path based on user type
    const from = route.query.from as string;
    let redirectPath = "/login";
    if (from === "tutor" || from === "tutor-register") {
      redirectPath = "/tutor-profile";
    } else if (from === "student" || from === "student-register") {
      redirectPath = "/student-profile";
    }
    
  } catch (error: any) {
    console.error("Email verification failed:", error);
    isEmailVerified.value = false;
    
    // Set appropriate error message
    if (error.code === "auth/invalid-action-code") {
      verificationError.value = "Invalid or expired verification link";
    } else if (error.code === "auth/expired-action-code") {
      verificationError.value = "Verification link has expired";
    } else {
      verificationError.value = "Verification failed. Please try again.";
    }
  }
};

// Update user verification status in Firestore
const updateUserVerificationStatus = async (email: string) => {
  try {
    // Find the user document by email
    const { collection, query, where, getDocs, updateDoc } = await import("firebase/firestore");
    const { db } = await import("@/firebase");
    
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", email));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      await updateDoc(userDoc.ref, {
        isEmailVerified: true,
        isVerified: true,
        updatedAt: new Date()
      });
      console.log("User verification status updated in Firestore");
    } else {
      console.warn("User document not found in Firestore for email:", email);
    }
  } catch (error) {
    console.error("Error updating user verification status:", error);
  }
};

// Determine where the user came from based on route query or referrer
const getBackRoute = () => {
  // Check if there's a 'from' query parameter
  const from = route.query.from as string;

  if (from === "tutor" || from === "tutor-register") {
    return "/tutor-register";
  } else if (from === "student" || from === "student-register") {
    return "/student-register";
  }

  // Fallback: check the document referrer
  const referrer = document.referrer;
  if (referrer.includes("tutor-register")) {
    return "/tutor-register";
  } else if (referrer.includes("student-register")) {
    return "/student-register";
  }

  // Default fallback to register role selection
  return "/register-role";
};

// Get redirect path based on user type
const getRedirectPath = () => {
  const from = route.query.from as string;
  if (from === "tutor" || from === "tutor-register") {
    return "/tutor-profile";
  } else if (from === "student" || from === "student-register") {
    return "/student-profile";
  }
  return "/login";
};

// Retry verification
const retryVerification = () => {
  verificationError.value = "";
  isEmailVerified.value = false;
  // The component will re-check for oobCode on mount
  router.push({
    path: '/email-verification',
    query: { 
      email: emailVerificationData.value.email,
      from: route.query.from
    }
  });
};

// Handle change email event
const handleChangeEmail = (data: any) => {
  console.log("Change email requested:", data);

  // Reset verification data
  emailVerificationData.value.verificationCode = "";

  // Go back to the appropriate registration form
  const backRoute = getBackRoute();
  router.push(backRoute);
};
</script>

<template>
  <div class="email-verification-page">
    <!-- Show success page if email is verified -->
    <EmailVerificationSuccess 
      v-if="isEmailVerified" 
      :auto-redirect="true"
      :redirect-delay="3000"
      :redirect-path="getRedirectPath()"
    />
    
    <!-- Show loading state when processing verification link -->
    <div v-else-if="isProcessingLink" class="loading-page">
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <h2 class="loading-title">Verifying your email...</h2>
        <p class="loading-text">Please wait while we verify your email address.</p>
      </div>
    </div>
    
    <!-- Show error page if verification failed -->
    <div v-else-if="verificationError" class="error-page">
      <div class="error-container">
        <div class="error-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#EF4444" stroke-width="2"/>
            <path d="M15 9l-6 6M9 9l6 6" stroke="#EF4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h1 class="error-title">Verification Failed</h1>
        <p class="error-message">{{ verificationError }}</p>
        <button @click="retryVerification" class="retry-btn">
          Try Again
        </button>
      </div>
    </div>
    
    <!-- Show regular verification page otherwise -->
    <template v-else>
      <Header />
      <main class="main-content">
        <!-- Back Arrow and Title Section -->
        <div class="header-section">
          <path
            d="M19 12H5M12 19L5 12L12 5"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />

          <div class="title-section">
            <h1 class="main-title">Verify Your Email</h1>
            <p class="subtitle">
              We've sent a verification code to your email address to complete your registration.
            </p>
          </div>
        </div>

        <!-- White Rectangle Container -->
        <div class="verification-container">
          <EmailVerification v-model="emailVerificationData" @change-email="handleChangeEmail" />
        </div>
      </main>
      <Footer />
    </template>
  </div>
</template>

<style scoped>
.email-verification-page {
  min-height: 100vh;
  background-image: url("/verify-email.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 13em 20px 15em 20px;
  min-height: calc(100vh - 200px);
}

.header-section {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 40px;
  margin-right: 40px;
  width: 100%;
  max-width: 550px;
}

.back-arrow {
  background: #cfcfcf;
  border: 2px solid #000;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-top: 10px;
}

.back-arrow:hover {
  background: #fff;
  transform: translateY(-2px);
}

.title-section {
  flex: 1;
  text-align: center;
}

.main-title {
  font-size: 48px;
  font-weight: 900;
  color: #000;
  margin: 0 0 12px 0;
  font-family: "Inter", sans-serif;
}

.subtitle {
  font-size: 18px;
  color: #000;
  margin: 0;
  font-style: italic;
  font-weight: 500;
  max-width: 500px;
  margin: 0 auto;
}

.verification-container {
  background: white;
  border: 4px solid #000;
  border-radius: 24px;
  box-shadow: 8px 8px 0px #000;
  width: 100%;
  max-width: 600px;
  padding: 0;
  overflow: hidden;
}

/* Override EmailVerification component styles to fit in container */
.verification-container :deep(.email-verification-container) {
  background: transparent;
  padding: 0;
  min-height: auto;
}

.verification-container :deep(.verification-wrapper) {
  max-width: none;
  width: 100%;
}

.verification-container :deep(.verification-card) {
  background: transparent;
  box-shadow: none;
  border-radius: 0;
  padding: 40px 32px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .main-content {
    padding: 100px 16px 20px 16px;
  }

  .header-section {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 30px;
    max-width: 100%;
    margin-right: 0;
  }

  .back-arrow {
    align-self: flex-start;
    width: 40px;
    height: 40px;
    margin-top: 0;
  }

  .main-title {
    font-size: 36px;
  }

  .subtitle {
    font-size: 16px;
  }

  .verification-container {
    max-width: 100%;
    border-radius: 16px;
    border-width: 3px;
    box-shadow: 6px 6px 0px #000;
  }

  .verification-container :deep(.verification-card) {
    padding: 32px 24px;
  }
}

@media (max-width: 480px) {
  .main-content {
    padding: 90px 12px 16px 12px;
  }

  .header-section {
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
    max-width: 100%;
    margin-right: 0;
  }

  .back-arrow {
    width: 36px;
    height: 36px;
    margin-top: 0;
  }

  .back-arrow svg {
    width: 18px;
    height: 18px;
  }

  .main-title {
    font-size: 28px;
    line-height: 1.1;
  }

  .subtitle {
    font-size: 14px;
    line-height: 1.4;
  }

  .verification-container {
    border-radius: 12px;
    border-width: 2px;
    box-shadow: 4px 4px 0px #000;
  }

  .verification-container :deep(.verification-card) {
    padding: 24px 20px;
  }

  .verification-container :deep(.main-title) {
    font-size: 20px !important;
  }

  .verification-container :deep(.instruction-text) {
    font-size: 13px !important;
  }

  .verification-container :deep(.complete-registration-btn) {
    padding: 12px 20px !important;
    font-size: 14px !important;
  }

  .verification-container :deep(.change-email-btn) {
    padding: 12px 20px !important;
    font-size: 14px !important;
  }
}

@media (max-width: 360px) {
  .main-content {
    padding: 80px 8px 12px 8px;
  }

  .header-section {
    gap: 8px;
    margin-bottom: 16px;
  }

  .back-arrow {
    width: 32px;
    height: 32px;
  }

  .back-arrow svg {
    width: 16px;
    height: 16px;
  }

  .main-title {
    font-size: 24px;
    line-height: 1.1;
  }

  .subtitle {
    font-size: 13px;
    line-height: 1.3;
  }

  .verification-container {
    border-radius: 10px;
    border-width: 2px;
    box-shadow: 3px 3px 0px #000;
  }

  .verification-container :deep(.verification-card) {
    padding: 20px 16px;
  }

  .verification-container :deep(.envelope-icon) {
    width: 60px !important;
    height: 60px !important;
    margin-bottom: 20px !important;
  }

  .verification-container :deep(.notification-circle) {
    width: 24px !important;
    height: 24px !important;
    font-size: 12px !important;
  }

  .verification-container :deep(.main-title) {
    font-size: 18px !important;
    margin-bottom: 16px !important;
  }

  .verification-container :deep(.instruction-text) {
    font-size: 12px !important;
    margin-bottom: 30px !important;
  }

  .verification-container :deep(.action-buttons) {
    gap: 12px !important;
  }

  .verification-container :deep(.complete-registration-btn) {
    padding: 10px 16px !important;
    font-size: 13px !important;
  }

  .verification-container :deep(.change-email-btn) {
    padding: 10px 16px !important;
    font-size: 13px !important;
  }
}

/* Loading Page Styles */
.loading-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 20px;
}

.loading-container {
  text-align: center;
  max-width: 400px;
  width: 100%;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  margin: 0 auto 32px auto;
  border: 4px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-title {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
}

.loading-text {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
}

/* Error Page Styles */
.error-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 20px;
}

.error-container {
  text-align: center;
  max-width: 400px;
  width: 100%;
}

.error-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 32px auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-icon svg {
  width: 100%;
  height: 100%;
}

.error-title {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
}

.error-message {
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 32px 0;
  line-height: 1.5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
}

.retry-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
}

.retry-btn:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

.retry-btn:active {
  transform: translateY(0);
}

@media (max-width: 480px) {
  .error-container {
    max-width: 100%;
  }
  
  .error-icon {
    width: 64px;
    height: 64px;
    margin-bottom: 24px;
  }
  
  .error-title {
    font-size: 28px;
  }
  
  .error-message {
    font-size: 14px;
  }
  
  .retry-btn {
    padding: 10px 20px;
    font-size: 14px;
  }
}
</style>
